import { Box, Divider, ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import {
  CallHospitalIcon, CancelAppointmentIcon, ClinicLocationIcon,
  RescheduleIcon, UrgentVisitIcon,
} from "../Icons";
import React, { FC, useState } from "react";

import { AvatarCustom } from "..";
import { GvCustomMenu } from "../../styles/styledComponents";
import { IAppointmentDetails } from "../../services/appointment-list/appointment-list.interface";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { ASSIGNED_BOOKING_TYPE, CREATE_BOOKING_TYPE, MODALS } from "../../config/params";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { RESCHEDULE_SOURCE } from "../../navigation/params";
import { fromUnixToFullDateNum, getDateEpochInTimezone } from "../../utils/unix-timestamp";
import { parseReasonsForVisit } from "../../shared/containers/AppointmentReviewContainer/AppointmentReviewContainer";
import { getContactString } from "../../shared/containers/ChangeHospitalContainer/ChangeHospitalContainer";
import { rescheduleSourceActions } from "../../state/recheduleSource";
import { useAppointmentStore } from "../../hooks/redux-hooks/appointment-store/appointment.store";
import { useDispatch } from "react-redux";
import { useModal } from "../../context/ModalContext";
import { usePetStore } from "../../hooks/redux-hooks/pet-store/pet-store.service";
import { userActivityLogger } from "../../utils/mixpanel";
import { useBookingAppointmentTypeStore, useRescheduleAllow } from "../../hooks";
import { useUrgentVisitStore } from "../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";
import { UrgentVisitStep } from "../../state/urgent-visit";

export const getApptTypeLabel = (createType: number | string): string => {
  switch (createType) {
  case ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING:
  case CREATE_BOOKING_TYPE.EXPRESS_BOOKING:
    return "express_visit";
  case ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING:
  case CREATE_BOOKING_TYPE.DROPOFF_BOOKING:
    return "drop_off";
  case ASSIGNED_BOOKING_TYPE.URGENT_BOOKING:
  case CREATE_BOOKING_TYPE.URGENT_BOOKING:
    return "urgent_visit";
  default:
    return "in_room_visit";
  }
};

type IAppointmentCard = {
  visit: IAppointmentDetails;
  isMenu?: boolean;
}

const AppointmentCard: FC<IAppointmentCard> = (props) => {
  const { visit, isMenu = false } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { petList }=usePetStore();
  const { openModal } = useModal();
  const dispatch = useDispatch();
  const { setSelectedVisit, setAppointmentDetails, setAppointmentsForm,
    setGroupedAppointments } = useAppointmentStore();
  const {
    setDefaultBookingType,
    setBookingAppointmentType,
    setAppointmentTypeIndicator,
  } = useBookingAppointmentTypeStore();
  const {
    resetUrgentVisit,
    setSelectedPets: setUrgentSelectedPets,
    setStep: setUrgentStep,
    setRescheduleAppointmentId,
    setSelectedDate,
    setPetReasons,
    setPetNotes,
  } = useUrgentVisitStore();

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReschedule = () => {
    setSelectedVisit(visit);
    setAppointmentDetails(visit);
    userActivityLogger(MIXPANEL_EVENTS.RESCHEDULE_VISIT, {
      referred_from: "Dashboard",
      pet_name: visit.pet.name,
      appt_type: getApptTypeLabel(
        visit.appointmentCreateType),
    });

    if (visit?.appointmentCreateType ===
      ASSIGNED_BOOKING_TYPE.URGENT_BOOKING) {
      setBookingAppointmentType(
        CREATE_BOOKING_TYPE.URGENT_BOOKING);
      setAppointmentTypeIndicator(visit.appointmentCreateType);
      resetUrgentVisit();
      setSelectedDate(getDateEpochInTimezone(
        visit.start_at, visit.location.timeZone,
      ));
      setRescheduleAppointmentId(visit.id);
      const petToSelect = (petList || []).find((p) => p.id === visit.pet.id);

      if (petToSelect) {
        setUrgentSelectedPets([petToSelect]);
        setUrgentStep(UrgentVisitStep.TIME_SELECTION);
        if (visit.reasonsForVisit) {
          const parsed = parseReasonsForVisit(visit.reasonsForVisit);
          setPetReasons(petToSelect.id, parsed.reasons);
          setPetNotes(petToSelect.id, parsed.notes);
        }
      }
      openModal(MODALS.URGENT_VISIT_MODAL);
    } else if (visit.appointmentCreateType === ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING) {
      setBookingAppointmentType(
        CREATE_BOOKING_TYPE.DROPOFF_BOOKING);
      setAppointmentTypeIndicator(visit.appointmentCreateType);
      openModal(MODALS.RESCHEDULE_A_VISIT);
    } else if (visit?.appointmentCreateType ===
      ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING) {
      setDefaultBookingType();
      openModal(MODALS.RESCHEDULE_EXPRESS_VISIT_MODAL);
    } else {
      setDefaultBookingType();
      openModal(MODALS.RESCHEDULE_A_VISIT);
    }
  };

  const handleCancel = () => {
    setSelectedVisit(visit);
    setAppointmentDetails(visit);
    setAppointmentTypeIndicator(visit.appointmentCreateType);
    openModal(MODALS.VISIT_CANCEL);
  };

  // const formattedDateObject = fromISOToFullDateObject(visit.dateTime);
  const formattedDateObject2 = fromUnixToFullDateNum(visit?.start_at!,
    visit?.location.timeZone);
  const isUrgent = visit.appointmentCreateType ===
    ASSIGNED_BOOKING_TYPE.URGENT_BOOKING;
  const { rescheduleAllowed } = useRescheduleAllow(visit?.start_at, visit?.location.timeZone);

  const handleVisitSelected = () => {
    setAppointmentsForm(null);
    setGroupedAppointments(null);
    setSelectedVisit(visit);
    setAppointmentDetails(visit);
    userActivityLogger(MIXPANEL_EVENTS.USER_CLICK_UPCOMING_VISIT,
      { owner_type: petList?.length==1?"single_pet":"multipet", no_of_pets: 1,
      });

    if (visit.appointmentCreateType === ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING) {
      setBookingAppointmentType(CREATE_BOOKING_TYPE.DROPOFF_BOOKING);
      setAppointmentTypeIndicator(visit.appointmentCreateType);
    } else {
      setDefaultBookingType();
    }
    openModal(MODALS.APPOINTMENT_REVIEW);
    dispatch(rescheduleSourceActions.setRescheduleSourceAction({ sourcePage: RESCHEDULE_SOURCE.DASHBOARD }));
  };

  return (
    <Box className="bg-white rounded-2xl p-2 cursor-pointer border border-transparent
    hover:border hover:border-dark-gv-yellow hover:bg-gv-yellow-light my-2 shadow-sm">
      <Box className="flex justify-between">
        <Box className="flex gap-4 items-center grow min-w-0" onClick={handleVisitSelected}>
          <Box className="relative" sx={{ flexShrink: 0 }}>
            <AvatarCustom
              image={visit.pet.image}
              width={56}
              height={56}
              name={visit.pet?.name}
            />
            {isUrgent && (
              <Box
                className="absolute bg-taupe-4 rounded-full
                  w-6 h-6 flex items-center justify-center"
                sx={{ bottom: -4, right: -4, zIndex: 10 }}
              >
                <UrgentVisitIcon width={16} height={16} />
              </Box>
            )}
          </Box>
          <Box className="flex flex-col gap-1 min-w-0">
            <Box className="flex gap-1 items-center border-b-2 border-dark-gv-yellow py-1 w-fit">
              <Typography variant="body1" fontWeight={600}>{formattedDateObject2.weekday}</Typography>
              <hr className="text-gray-3 h-3 border-x" />
              <Typography variant="body1" className="flex gap-1 items-center">
                <span>{formattedDateObject2.month}</span>
                <span>{formattedDateObject2.day}</span>
                <span>&#x2022;</span>
                <span>
                  {formattedDateObject2.time}
                </span>
              </Typography>
            </Box>
            <Typography
              variant="h3"
              className="font-semibold"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {visit.pet?.name}&apos;s <span className="capitalize">{visit.appointmentType.split("-")[0]}</span>
            </Typography>
            <Box className="flex items-center gap-1">
              <ClinicLocationIcon width={14} height={14} color="#908E8D" />
              <Typography variant="body1" sx={{ color: (theme) => theme.palette.gray.dark }}>
                {visit.location?.locationName}</Typography>
            </Box>
          </Box>
        </Box>
        <div id="appointment-button" aria-haspopup="true"
          aria-controls={open ? "appointment-menu" : undefined}
          aria-expanded={open ? "true" : undefined} onClick={handleClick}>
          <MoreHorizIcon sx={{ cursor: "pointer" }} />
        </div>
        {isMenu &&
          <GvCustomMenu autoFocus={false} transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "top" }}
            id="appointment-menu" anchorEl={anchorEl} open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "appointment-button",
            }}
            classes={{ list: "!p-0" }}
          >
            {(visit.allowedToEdit || (isUrgent && rescheduleAllowed)) &&
            <MenuItem
              onClick={() => {
                handleReschedule();
                handleClose();
              }}
            >
              <ListItemIcon>
                <RescheduleIcon />
              </ListItemIcon>
              <ListItemText>Reschedule</ListItemText>
            </MenuItem>}
            <Divider className="w-full !m-0" />
            {(visit.allowedToEdit || (isUrgent && rescheduleAllowed)) &&
            <MenuItem
              onClick={() => {
                handleCancel();
                handleClose();
              }}
            >
              <ListItemIcon>
                <CancelAppointmentIcon />
              </ListItemIcon>
              <ListItemText>Cancel</ListItemText>
            </MenuItem>}
            <Divider className="w-full !m-0" />
            <MenuItem
              onClick={() => {
                handleClose();
              }}
            >
              <ListItemIcon>
                <CallHospitalIcon />
              </ListItemIcon>
              <ListItemText><a href={getContactString(visit.location.phoneNumber)}>Call Hospital</a></ListItemText>
            </MenuItem>
          </GvCustomMenu>}
      </Box>
    </Box>
  );
};

export default AppointmentCard;
